import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import Navbar from './Navbar';
import Sidebar from './Sidebar';
import { useFitRecords } from './useFitRecords';
import * as FitRecords from './fitRecordStore';
import FitReport, { buildReportModel, downloadReportExcel, downloadReportPdf } from './FitReport';
import '../styles/Dashboard.css';

// ────────────────────────────────────────────────────────────────────────
// RUN HISTORY — the "separate screen" from the client call (item #25):
// the dashboard keeps short summaries, this page holds the full list so
// the dashboard doesn't turn into a data dump.
//
// Route to register (App.js):   <Route path="/run-history" element={<RunHistoryPage />} />
//
// Reports page (client call 8-Oct-2026): every saved run is a report that can be
// downloaded as Excel or PDF straight from its row, and the whole list is
// searchable (name, system, model, status, date, R²). The list is per login:
// the backend only returns fit_records owned by the signed-in user.
//
// Shows saved runs across ALL of the user's reaction systems (item #24),
// grouped by system, user's own fits before example runs. Each row can be
// summarised, reopened in Optimize with its saved inputs (item #23),
// deleted, or ticked for the Compare Fits page.
// ────────────────────────────────────────────────────────────────────────

const ROWS_PER_PAGE = 5;   // per group, before "Show N more" — keeps the page short

const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'own', label: 'My fits' },
  { key: 'example', label: 'Examples' },
];

const statusPill = (s) => (s === 'converged' ? 'pill-done' : s === 'stopped' ? 'pill-wait' : 'pill-run');

const fmtDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString(undefined, { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

// ── "Active" run (client item 2) ───────────────────────────────────────────────
// MEANING: the Active run is the saved fit currently loaded as the user's working fit
// in Data Fitting. It is the one that was last (a) saved by a new run or (b) re-opened
// with the Reopen button. There is ONE per user across ALL reaction systems, never one
// per system and never several.
//
// WHY MORE THAN ONE SHOWED: the page used to draw an ACTIVE tag for every record whose
// `is_active` flag was true. Setting a fit active only clears the flag within its own
// reaction system / list, so a second system (or a stale record) kept its old flag and
// two runs showed ACTIVE. The page now picks a single winner from those records and from
// the last Reopen, so it can never show more than one, whatever the flags say.
const activeMarkerKey = () => `kq_active_fit_${localStorage.getItem('email') || 'user'}`;

const readActiveMarker = () => {
  try {
    const m = JSON.parse(localStorage.getItem(activeMarkerKey()) || 'null');
    return m && m.uuid && Number.isFinite(m.at) ? m : null;
  } catch { return null; }
};

const writeActiveMarker = (uuid) => {
  try {
    if (uuid) localStorage.setItem(activeMarkerKey(), JSON.stringify({ uuid, at: Date.now() }));
    else localStorage.removeItem(activeMarkerKey());
  } catch { /* storage unavailable: the flags still decide */ }
};

const stampOf = (f) => {
  const t = new Date(f.activated_at || f.last_opened_at || f.updated_at || f.created_at || 0).getTime();
  return Number.isFinite(t) ? t : 0;
};

// The single Active run: the latest of (flagged records, last Reopen). null when none.
const pickActiveUuid = (rows) => {
  let best = null;     // { uuid, at }
  rows.forEach((f) => {
    if (f.is_active && (!best || stampOf(f) > best.at)) best = { uuid: f.fit_uuid, at: stampOf(f) };
  });
  const marker = readActiveMarker();
  if (marker && rows.some((f) => f.fit_uuid === marker.uuid) && (!best || marker.at >= best.at)) {
    best = marker;
  }
  return best ? best.uuid : null;
};

const systemNameOf = (f, fallback) =>
  f.system_name || f.reaction_system_name || fallback || 'Reaction system';

// A system's stable identity, so renaming never splits or merges groups.
const systemKeyOf = (f, fallback) => f.system_uuid || systemNameOf(f, fallback);

// Everything a user might type to find a report: name, system, model,
// reversibility, status, example/own, the date as shown AND as ISO, and R².
const searchTextOf = (f, fallbackSystem) => [
  f.label,
  systemNameOf(f, fallbackSystem),
  f.model_type === 'lhhw' ? 'lhhw' : 'power law powerlaw',
  f.reversibility === 'irreversible' ? 'irreversible irrev' : 'reversible rev',
  f.status,
  f.is_example ? 'example' : 'my fit own',
  fmtDate(f.created_at),
  (f.created_at || '').slice(0, 10),
  f.overall_r2 != null ? Number(f.overall_r2).toFixed(4) : '',
].join(' ').toLowerCase();

const RunHistoryPage = () => {
  const navigate = useNavigate();
  const state = useFitRecords();
  const { fits, allFits, allFitsScope, loading, error, reactionSystemName } = state;

  const [filter, setFilter] = useState('all');
  const [query, setQuery] = useState('');
  const [openUuid, setOpenUuid] = useState(null);   // fit_uuid currently expanded
  const [openGroups, setOpenGroups] = useState({});   // { [systemName]: true } — collapsed by default
  const [visibleCounts, setVisibleCounts] = useState({});   // { [systemName]: N } — pagination within a group
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [downloading, setDownloading] = useState(null);   // `${fit_uuid}:xlsx|pdf` while fetching
  const [editingKey, setEditingKey] = useState(null);          // system being renamed
  const [draftName, setDraftName] = useState('');
  const [menu, setMenu] = useState(null);                      // right-click menu { x, y, group }
  const cancelRenameRef = useRef(false);
  const summaryRef = useRef(null);                          // the open Summary panel
  const [closeSlot, setCloseSlot] = useState(null);         // { host, className } - where the Close button is mounted

  useEffect(() => {
    if (!localStorage.getItem('role')) { navigate('/'); return; }
    FitRecords.bootstrapFitRecords();
  }, [navigate]);

  const rows = allFitsScope === 'all' ? allFits : fits;

  // Bumped on Reopen / delete so the single Active run is recomputed straight away
  // (the stored marker is not React state).
  const [activeTick, setActiveTick] = useState(0);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const activeUuid = useMemo(() => pickActiveUuid(rows), [rows, activeTick]);

  // Grouped by system; inside a group: own fits (newest first), then examples.
  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    const matches = rows.filter((f) => {
      if (filter === 'own' && f.is_example) return false;
      if (filter === 'example' && !f.is_example) return false;
      if (q && !q.split(/\s+/).every((term) => searchTextOf(f, reactionSystemName).includes(term))) return false;
      return true;
    });
    const bySystem = new Map();
    matches.forEach((f) => {
      const key = systemKeyOf(f, reactionSystemName);
      if (!bySystem.has(key)) bySystem.set(key, { name: systemNameOf(f, reactionSystemName), uuid: f.system_uuid || null, list: [] });
      bySystem.get(key).list.push(f);
    });
    const newest = (a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0);
    return [...bySystem.entries()].map(([key, { name, uuid, list }]) => ({
      key,
      name,
      uuid,
      runs: [...list.filter((f) => !f.is_example).sort(newest), ...list.filter((f) => f.is_example).sort(newest)],
    }));
  }, [rows, filter, query, reactionSystemName]);

  const totalShown = groups.reduce((n, g) => n + g.runs.length, 0);

  // First group open by default (usually the current/most-recent system);
  // everything else starts collapsed — this is most of the scroll reduction.
  useEffect(() => {
    if (groups.length && Object.keys(openGroups).length === 0) {
      setOpenGroups({ [groups[0].key]: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups.length]);

  const toggleGroup = (name) => setOpenGroups((prev) => ({ ...prev, [name]: !prev[name] }));
  // Rename a reaction system (Reports page). Saved on the backend through the
  // store, which also relabels every list so the new name shows immediately.
  const startRename = (g) => {
    if (!g.uuid) return;
    cancelRenameRef.current = false;
    setMenu(null); setEditingKey(g.key); setDraftName(g.name);
  };
  const saveRename = async (g) => {
    if (cancelRenameRef.current) { cancelRenameRef.current = false; return; }
    const next = draftName.trim();
    setEditingKey(null);
    if (!next || next === g.name) return;
    const ok = await FitRecords.renameReactionSystemByUuid(g.uuid, next);
    if (!ok) window.alert("Couldn't rename this reaction system. Please try again.");
  };

  // close the right-click menu on any click / Esc
  useEffect(() => {
    if (!menu) return undefined;
    const close = () => setMenu(null);
    const onKey = (e) => { if (e.key === 'Escape') setMenu(null); };
    window.addEventListener('click', close);
    window.addEventListener('keydown', onKey);
    return () => { window.removeEventListener('click', close); window.removeEventListener('keydown', onKey); };
  }, [menu]);

  const showMore = (name) => setVisibleCounts((prev) => ({ ...prev, [name]: (prev[name] || ROWS_PER_PAGE) + ROWS_PER_PAGE }));

  // Close the Summary panel (X / Close button, or the Escape key).
  const closeSummary = () => { setOpenUuid(null); setDetail(null); setDetailLoading(false); };

  useEffect(() => {
    if (!openUuid) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') closeSummary(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [openUuid]);

  // Client: put Close on the SAME line as the report's action buttons, in the order
  // "Download Excel", "Download PDF", "Close". Those two buttons are rendered by
  // FitReport, so once the report has loaded we find the Download PDF button and mount
  // the Close button right after it (as a sibling, copying its look). If they can't be
  // found, a Close button is shown above the report instead so it is never missing.
  useEffect(() => {
    setCloseSlot(null);
    if (!openUuid || detailLoading || !detail) return undefined;
    const root = summaryRef.current;
    if (!root) return undefined;
    const pdfBtn = Array.from(root.querySelectorAll('button, a'))
      .find((el) => /download\s*pdf/i.test(el.textContent || ''));
    const row = pdfBtn && pdfBtn.parentElement;
    if (!row) return undefined;
    const host = document.createElement('span');
    host.style.display = 'contents';
    row.appendChild(host);
    setCloseSlot({ host, className: pdfBtn.className });
    return () => { setCloseSlot(null); host.remove(); };
  }, [openUuid, detail, detailLoading]);

  const toggleSummary = async (uuid) => {
    if (openUuid === uuid) { setOpenUuid(null); setDetail(null); return; }
    setOpenUuid(uuid);
    setDetail(null);
    setDetailLoading(true);
    setDetail(await FitRecords.fetchFitDetail(uuid));
    setDetailLoading(false);
  };

  // One-click Excel / PDF from the row: fetch the full saved run, build the
  // same report model the summary panel uses, then download it.
  const handleDownload = async (f, kind) => {
    const key = `${f.fit_uuid}:${kind}`;
    setDownloading(key);
    try {
      const d = await FitRecords.fetchFitDetail(f.fit_uuid);
      const model = buildReportModel(d);
      if (!model) { window.alert("Couldn't load this report."); return; }
      if (kind === 'xlsx') downloadReportExcel(model); else downloadReportPdf(model);
    } finally {
      setDownloading(null);
    }
  };

  const handleOpenInInput = async (uuid) => {
    // Reopen makes THIS run the one Active run. The marker is written first so the tag is
    // already right if the user comes straight back to this page; the store call updates
    // the saved record.
    writeActiveMarker(uuid);
    setActiveTick((n) => n + 1);
    await FitRecords.setActiveFit(uuid);
    await FitRecords.stageRestore(uuid);   // DataFittingPage consumes this on mount
    navigate('/data-fitting#input');
  };

  const handleDelete = async (f) => {
    if (!window.confirm(`Delete "${f.label || 'this run'}"? This can't be undone.`)) return;
    await FitRecords.deleteFitRecord(f.fit_uuid);
    const marker = readActiveMarker();
    if (marker && marker.uuid === f.fit_uuid) writeActiveMarker(null);
    setActiveTick((n) => n + 1);
    if (openUuid === f.fit_uuid) { setOpenUuid(null); setDetail(null); }
  };

  return (
    <div style={styles.shell}>
      <Sidebar />
      <div style={styles.rightCol}>
        <Navbar activePage="app" />
        <div style={styles.pageContent}>
          <div className="dash-page">

            <div className="rh-head">
              <div>
                <button className="dash-view-all" onClick={() => navigate('/dashboard')}>
                  <i className="ti ti-arrow-left" aria-hidden="true" /> Back to dashboard
                </button>
                <h1 className="rh-title">Reports</h1>
                <p className="rh-sub">
                  {totalShown} report{totalShown === 1 ? '' : 's'} across {groups.length} system{groups.length === 1 ? '' : 's'} &middot; saved to your login
                </p>
              </div>
              <div className="rh-head-actions">
                {state.selectedFitUuids.length >= 1 && (
                  <button className="dash-hero-btn dash-hero-btn--primary rh-btn" onClick={() => navigate('/compare-fits')}>
                    <i className="ti ti-chart-line" aria-hidden="true" />
                    Compare selected ({state.selectedFitUuids.length})
                  </button>
                )}
              </div>
            </div>

            <div className="rh-toolbar">
              <div className="rh-filters">
                {FILTERS.map((f) => (
                  <button
                    key={f.key}
                    className={`rh-filter ${filter === f.key ? 'rh-filter--on' : ''}`}
                    onClick={() => setFilter(f.key)}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
              <input
                className="rh-search"
                type="search"
                placeholder="Search reports by name, system, model, status or date…" aria-label="Search reports"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>

            {allFitsScope !== 'all' && rows.length > 0 && (
              <p className="rh-note">
                Showing runs for the current reaction system only.
              </p>
            )}
            {error && <p className="dash-summary-error">Couldn't load reports: {error}</p>}
            {loading && rows.length === 0 && <p className="dash-summary-text">Loading saved runs…</p>}
            {!loading && !error && rows.length === 0 && (
              <div className="dash-summary-card dash-summary-card--empty">
                <i className="ti ti-folder-plus dash-summary-icon" aria-hidden="true" />
                <p className="dash-summary-text">No saved reports yet. Run a fit and use “Save this fit” — it will show up here.</p>
                <button className="dash-summary-cta" onClick={() => navigate('/data-fitting')}>Run First System</button>
              </div>
            )}
            {rows.length > 0 && totalShown === 0 && (
              <p className="dash-summary-text">No reports match your search.</p>
            )}

            {groups.map((g) => {
              // While searching, open every group and drop the "show more"
              // cap so a match can never be hidden inside a collapsed group.
              const searching = query.trim().length > 0;
              const isOpen = searching || Boolean(openGroups[g.key]);
              const limit = searching ? g.runs.length : (visibleCounts[g.key] || ROWS_PER_PAGE);
              const shown = g.runs.slice(0, limit);
              const remaining = g.runs.length - shown.length;
              return (
                <div key={g.key} className="rh-group">
                  <div
                    role="button"
                    tabIndex={0}
                    className="rh-group-header"
                    style={{ cursor: 'pointer', alignItems: 'center', padding: '6px 14px' }}
                    onClick={() => editingKey !== g.key && toggleGroup(g.key)}
                    onKeyDown={(e) => {
                      if (editingKey === g.key) return;
                      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleGroup(g.key); }
                      if (e.key === 'F2') startRename(g);
                    }}
                    onContextMenu={(e) => { e.preventDefault(); setMenu({ x: e.clientX, y: e.clientY, group: g }); }}
                    aria-expanded={isOpen}
                    title={g.uuid ? 'Right-click to rename' : undefined}
                  >
                    <i className={`ti ti-chevron-right rh-group-caret ${isOpen ? 'rh-group-caret--open' : ''}`} aria-hidden="true" />
                    <i className="ti ti-flask rh-group-icon" aria-hidden="true" />
                    <span style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0, textAlign: 'left' }}>
                      {editingKey === g.key ? (
                        <input
                          autoFocus
                          className="rh-search"
                          style={{ width: '100%', maxWidth: 360, padding: '4px 10px' }}
                          value={draftName}
                          maxLength={60}
                          aria-label="Reaction system name"
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) => setDraftName(e.target.value)}
                          onBlur={() => saveRename(g)}
                          onKeyDown={(e) => {
                            e.stopPropagation();
                            if (e.key === 'Enter') e.currentTarget.blur();
                            if (e.key === 'Escape') { cancelRenameRef.current = true; setEditingKey(null); }
                          }}
                        />
                      ) : (
                        <span className="rh-group-name">{g.name}</span>
                      )}
                    </span>
                    {g.uuid && editingKey !== g.key && (
                      <button
                        type="button"
                        className="rh-action"
                        title="Rename reaction system"
                        style={{ width: 26, height: 26, minWidth: 26, minHeight: 26, padding: 0, fontSize: 14, lineHeight: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
                        aria-label={`Rename ${g.name}`}
                        onClick={(e) => { e.stopPropagation(); startRename(g); }}
                      >
                        <i className="ti ti-pencil" aria-hidden="true" />
                      </button>
                    )}
                    <span className="dash-count">{g.runs.length}</span>
                  </div>

                  {isOpen && (
                    <div className="rh-group-body">
                      <div className="rh-row rh-row--head" aria-hidden="true">
                        <span />
                        <span>Run</span>
                        <span>Model</span>
                        <span>R&sup2;</span>
                        <span>Date</span>
                        <span>Status</span>
                        <span>Actions</span>
                      </div>

                      {shown.map((f) => (
                        <div key={f.fit_uuid} className="rh-row-wrap">
                          <div className="rh-row">
                            <input
                              type="checkbox"
                              checked={state.selectedFitUuids.includes(f.fit_uuid)}
                              onChange={() => FitRecords.toggleFitSelected(f.fit_uuid)}
                              aria-label={`Select ${f.label || 'run'} for comparison`}
                            />
                            <span className="rh-name">
                              <span className="rh-name-text">{f.label || 'Untitled run'}</span>
                              {f.fit_uuid === activeUuid && <span className="dash-status-pill pill-done rh-tag">ACTIVE</span>}
                              {f.is_example && <span className="dash-example-tag">Example</span>}
                            </span>
                            <span className="rh-meta">
                              {f.model_type === 'lhhw' ? 'LHHW' : 'Power Law'} <span className="rh-dim">· {f.reversibility === 'irreversible' ? 'Irrev.' : 'Rev.'}</span>
                            </span>
                            <span className="rh-meta rh-r2">{f.overall_r2 != null ? Number(f.overall_r2).toFixed(4) : '—'}</span>
                            <span className="rh-meta rh-date">{fmtDate(f.created_at)}</span>
                            <span className={`dash-status-pill ${statusPill(f.status)}`}>{f.status}</span>
                            <span className="rh-actions">
                              <button className="rh-action" onClick={() => toggleSummary(f.fit_uuid)} title="Summary">
                                <i className="ti ti-file-text" aria-hidden="true" />
                              </button>
                              <button
                                className="rh-action"
                                onClick={() => handleDownload(f, 'xlsx')}
                                disabled={downloading === `${f.fit_uuid}:xlsx`}
                                title="Download Excel"
                                aria-label="Download Excel report"
                              >
                                <i className="ti ti-file-spreadsheet" aria-hidden="true" />
                              </button>
                              <button
                                className="rh-action"
                                onClick={() => handleDownload(f, 'pdf')}
                                disabled={downloading === `${f.fit_uuid}:pdf`}
                                title="Download PDF"
                                aria-label="Download PDF report"
                              >
                                <i className="ti ti-file-type-pdf" aria-hidden="true" />
                              </button>
                              <button className="rh-action" onClick={() => handleOpenInInput(f.fit_uuid)} title="Reopen">
                                <i className="ti ti-folder-open" aria-hidden="true" />
                              </button>
                              <button className="rh-action rh-action--danger" onClick={() => handleDelete(f)} title="Delete">
                                <i className="ti ti-trash" aria-hidden="true" />
                              </button>
                            </span>
                          </div>

                          {openUuid === f.fit_uuid && (
                            <div className="rh-summary" ref={summaryRef}>
                              {/* Fallback only: used until/unless Close can sit beside Download PDF. */}
                              {!closeSlot && !detailLoading && (
                                <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '6px 8px 0' }}>
                                  <button
                                    type="button"
                                    className="rh-action"
                                    onClick={closeSummary}
                                    title="Close summary"
                                    aria-label="Close summary"
                                    style={{ width: 'auto', padding: '0 12px', gap: 6, display: 'inline-flex', alignItems: 'center' }}
                                  >
                                    <i className="ti ti-x" aria-hidden="true" /> Close
                                  </button>
                                </div>
                              )}
                              {closeSlot && createPortal(
                                <button
                                  type="button"
                                  className={closeSlot.className}
                                  onClick={closeSummary}
                                  title="Close summary"
                                  aria-label="Close summary"
                                >
                                  <i className="ti ti-x" aria-hidden="true" /> Close
                                </button>,
                                closeSlot.host
                              )}
                              <FitReport detail={detail} loading={detailLoading} />
                            </div>
                          )}
                        </div>
                      ))}

                      {remaining > 0 && (
                        <button className="rh-more" onClick={() => showMore(g.key)}>
                          Show {Math.min(remaining, ROWS_PER_PAGE)} more <i className="ti ti-chevron-down" aria-hidden="true" />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {menu && (
        <div
          role="menu"
          style={{
            position: 'fixed', top: menu.y, left: menu.x, zIndex: 1000, minWidth: 190,
            background: '#14294a', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 8,
            boxShadow: '0 8px 24px rgba(0,0,0,0.45)', padding: 4,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button" role="menuitem" className="rh-action"
            disabled={!menu.group.uuid}
            style={{ width: '100%', justifyContent: 'flex-start', gap: 8, padding: '8px 12px' }}
            onClick={() => startRename(menu.group)}
          >
            <i className="ti ti-pencil" aria-hidden="true" /> Rename
          </button>
        </div>
      )}
    </div>
  );
};

const styles = {
  shell: { display: 'flex', height: '100vh', overflow: 'hidden', width: '100%' },
  rightCol: {
    marginLeft: '220px', width: 'calc(100% - 220px)', display: 'flex',
    flexDirection: 'column', height: '100vh', overflow: 'hidden',
  },
  pageContent: { flex: 1, marginTop: '68px', overflowY: 'auto', overflowX: 'hidden', minHeight: 0 },
};

export default RunHistoryPage;